import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Quote } from '../types';
import { useAuth } from '../lib/AuthContext';
import { moderateContent } from '../lib/moderation';

export function useQuotes() {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, userRole } = useAuth();

  useEffect(() => {
    if (user) {
      fetchQuotes();
    }
  }, [user]);

  async function fetchQuotes() {
    try {
      setLoading(true);
      
      let query = supabase
        .from('quotes')
        .select('*')
        .order('created_at', { ascending: false });

      if (userRole === 'viewer') {
        query = query.eq('user_id', user?.id);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      setQuotes(data.map(quote => ({
        ...quote,
        id: quote.id,
        isFavorite: quote.is_favorite,
        quotewall: quote.quotewall
      })));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred while fetching quotes';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  async function addQuote(newQuote: Omit<Quote, 'id'>) {
    try {
      if (!user) {
        return {
          success: false,
          error: 'You must be logged in to add quotes'
        };
      }

      // Moderate content before saving
      const moderationResult = await moderateContent(newQuote.text);
      if (!moderationResult.isAcceptable) {
        return {
          success: false,
          error: 'Content violates community guidelines'
        };
      }

      const quoteData = {
        text: newQuote.text,
        author: newQuote.author,
        age: newQuote.age || null,
        location: newQuote.location || null,
        context: newQuote.context || null,
        year: newQuote.year || null,
        is_favorite: newQuote.isFavorite,
        quotewall: newQuote.quotewall,
        user_id: user.id
      };

      const { data, error } = await supabase
        .from('quotes')
        .insert([quoteData])
        .select()
        .single();

      if (error) {
        throw error;
      }

      setQuotes(prev => [{
        ...data,
        id: data.id,
        isFavorite: data.is_favorite,
        quotewall: data.quotewall
      }, ...prev]);

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add quote';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  async function updateQuote(id: string, updates: Partial<Quote>) {
    try {
      if (!user) {
        return {
          success: false,
          error: 'You must be logged in to update quotes'
        };
      }

      console.log('Updating quote:', { id, updates, userRole });

      const dbUpdates: any = {};
      
      if (updates.text !== undefined) dbUpdates.text = updates.text;
      if (updates.author !== undefined) dbUpdates.author = updates.author;
      if (updates.age !== undefined) dbUpdates.age = updates.age || null;
      if (updates.location !== undefined) dbUpdates.location = updates.location || null;
      if (updates.context !== undefined) dbUpdates.context = updates.context || null;
      if (updates.year !== undefined) dbUpdates.year = updates.year || null;
      if (typeof updates.isFavorite === 'boolean') dbUpdates.is_favorite = updates.isFavorite;
      if (typeof updates.quotewall === 'boolean') dbUpdates.quotewall = updates.quotewall;

      const { error } = await supabase
        .from('quotes')
        .update(dbUpdates)
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error updating quote:', error);
        throw error;
      }

      console.log('Successfully updated quote');

      setQuotes(prev => prev.map(quote => 
        quote.id === id ? { ...quote, ...updates } : quote
      ));

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update quote';
      console.error('Update quote error:', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  async function deleteQuotes(ids: string[]) {
    try {
      if (!user) {
        return {
          success: false,
          error: 'You must be logged in to delete quotes'
        };
      }

      console.log('Deleting quotes:', { ids });

      const { error } = await supabase
        .from('quotes')
        .delete()
        .in('id', ids)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting quotes:', error);
        throw error;
      }

      console.log('Successfully deleted quotes');

      setQuotes(prev => prev.filter(quote => !ids.includes(quote.id)));
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete quotes';
      console.error('Delete quotes error:', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  return {
    quotes,
    loading,
    error,
    addQuote,
    updateQuote,
    deleteQuotes,
    refresh: fetchQuotes,
    canAdd: userRole !== 'viewer',
    canEdit: userRole !== 'viewer',
    canDelete: true
  };
}